// src/components/useVoiceCommands.js
// vPTT-02  (Push-to-talk: 1 klik = 1 opname; stabiel voor MVP + punt fix)

import { useEffect, useMemo, useRef, useState } from "react";

function normalize(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

export default function useVoiceCommands({
  lang = "nl-NL",
  onCommand, // (cmd, rawHeard) => boolean
  onDictation, // (text) => void
  timeoutMs = 4500,
} = {}) {
  const SpeechRecognition =
    typeof window !== "undefined"
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;

  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [status, setStatus] = useState("");
  const [lastHeard, setLastHeard] = useState("");
  const [error, setError] = useState("");

  const recRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    setSupported(!!SpeechRecognition);
  }, [SpeechRecognition]);

  const parse = useMemo(() => {
    return (raw) => {
      const t0 = String(raw || "").trim();
      const t = normalize(t0);

      // Punt: soms komt het echt als "." binnen
      if (t0 === "." || t === "." || t === "punt" || t === "puntje") return { type: "punt" };

      // navigatie
      if (t === "volgende" || t === "next" || t === "tab" || t === "tap") return { type: "next" };
      if (t === "vorige" || t === "previous" || t === "back" || t === "terug" || t.startsWith("vorig"))
        return { type: "prev" };

      // acties
      if (t === "nieuw") return { type: "new" };
      if (t === "opslaan" || t === "bewaren") return { type: "save" };
      if (t === "verwijder" || t === "verwijderen" || t === "delete") return { type: "delete" };
      if (t === "vernieuw" || t === "vernieuwen" || t === "refresh") return { type: "refresh" };

      // selectie / navigatie
      if (t === "selecteer" || t === "item" || t === "rechts") return { type: "select" };
      if (t === "lijst" || t === "links") return { type: "list" };

      // misc
      if (t === "leeg" || t === "geen" || t === "blanco" || t === "null") return { type: "clear" };

      return null;
    };
  }, []);

  function clearTimer() {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }

  function safeStop() {
    clearTimer();
    try {
      recRef.current?.stop?.();
    } catch {}
    setListening(false);
    setStatus("");
  }

  function startOnce() {
    if (!SpeechRecognition) return;
    if (listening) return;

    setError("");
    setLastHeard("");
    setStatus("Luisteren…");
    setListening(true);

    try {
      recRef.current?.onresult && (recRef.current.onresult = null);
      recRef.current?.onend && (recRef.current.onend = null);
      recRef.current?.onerror && (recRef.current.onerror = null);
      recRef.current?.stop?.();
    } catch {}

    const rec = new SpeechRecognition();
    recRef.current = rec;

    rec.lang = lang;
    rec.interimResults = false;
    rec.continuous = false;

    timerRef.current = setTimeout(() => {
      setError("no-speech");
      safeStop();
    }, timeoutMs);

    rec.onstart = () => setStatus("Luisteren…");

    rec.onerror = (e) => {
      const code = e?.error || "speech_error";
      setError(code);
      safeStop();
    };

    rec.onend = () => {
      safeStop();
    };

    rec.onresult = (event) => {
      clearTimer();

      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        const txt = res?.[0]?.transcript || "";
        if (res.isFinal) final += txt;
      }

      const heard = String(final || "").trim();
      if (!heard) {
        setError("no-speech");
        safeStop();
        return;
      }

      setLastHeard(heard);
      setStatus("Verwerken…");

      const cmd = parse(heard);
      let handled = false;

      if (cmd && onCommand) {
        try {
          handled = !!onCommand(cmd, heard);
        } catch {
          handled = false;
        }
      }

      if (!handled && onDictation) {
        try {
          onDictation(heard);
        } catch {}
      }

      safeStop();
    };

    try {
      rec.start();
    } catch {
      setError("start_failed");
      safeStop();
    }
  }

  function stop() {
    safeStop();
  }

  return {
    supported,
    listening,
    status,
    lastHeard,
    error,
    startOnce,
    stop,
  };
}